// Average absolute prediction error for one player in one round (lower = better)
export function calcPredictionError(predictions, actualScores) {
  let total = 0;
  let count = 0;
  for (const [uid, predicted] of Object.entries(predictions)) {
    if (actualScores[uid] !== undefined) {
      total += Math.abs(predicted - actualScores[uid]);
      count++;
    }
  }
  return count > 0 ? total / count : null;
}

// Return uid(s) with lowest prediction error for a round
export function calcRoundWinners(playerIds, roundScores) {
  const actualScores = {};
  for (const [uid, data] of Object.entries(roundScores)) {
    if (data.actualScore !== undefined) actualScores[uid] = data.actualScore;
  }

  const errors = {};
  for (const uid of playerIds) {
    const data = roundScores[uid];
    if (!data?.predictions) continue;
    const otherActuals = {};
    for (const [ouid, odata] of Object.entries(roundScores)) {
      if (ouid !== uid && odata.actualScore !== undefined) {
        otherActuals[ouid] = odata.actualScore;
      }
    }
    errors[uid] = calcPredictionError(data.predictions, otherActuals);
  }

  const valid = Object.entries(errors).filter(([, e]) => e !== null);
  if (!valid.length) return [];
  const min = Math.min(...valid.map(([, e]) => e));
  return valid.filter(([, e]) => e === min).map(([uid]) => uid);
}

const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
const minKeys = (obj) => {
  const valid = Object.entries(obj).filter(([, v]) => v !== null);
  if (!valid.length) return [];
  const min = Math.min(...valid.map(([, v]) => v));
  return valid.filter(([, v]) => v === min).map(([k]) => k);
};
const maxKeys = (obj) => {
  const valid = Object.entries(obj).filter(([, v]) => v !== null);
  if (!valid.length) return [];
  const max = Math.max(...valid.map(([, v]) => v));
  return valid.filter(([, v]) => v === max).map(([k]) => k);
};

// Calculate all end-of-game award categories
// rounds: [{ id, chooserId, song: { title, artist } }]
// allRoundScores: { [roundId]: { [userId]: { actualScore, predictions } } }
export function calcGameStats(playerIds, rounds, allRoundScores) {
  const roundWins = Object.fromEntries(playerIds.map((uid) => [uid, 0]));
  const predErrors = Object.fromEntries(playerIds.map((uid) => [uid, []]));
  const beingPredictedErrors = Object.fromEntries(playerIds.map((uid) => [uid, []]));
  const perTargetErrors = Object.fromEntries(playerIds.map((uid) => [uid, {}]));
  const songRatings = {};

  for (const round of rounds) {
    const scores = allRoundScores[round.id] || {};

    const actuals = {};
    for (const [uid, data] of Object.entries(scores)) {
      if (data.actualScore !== undefined) actuals[uid] = data.actualScore;
    }

    // Round winners
    const winners = calcRoundWinners(playerIds, scores);
    for (const uid of winners) {
      if (roundWins[uid] !== undefined) roundWins[uid]++;
    }

    // Prediction accuracy per player (overall + per-target)
    for (const uid of playerIds) {
      const data = scores[uid];
      if (!data?.predictions) continue;
      const otherActuals = {};
      for (const [ouid, odata] of Object.entries(scores)) {
        if (ouid !== uid && odata.actualScore !== undefined) otherActuals[ouid] = odata.actualScore;
      }
      const err = calcPredictionError(data.predictions, otherActuals);
      if (err !== null) predErrors[uid].push(err);

      // Per-target: how well uid predicted each specific other player
      for (const [targetUid, predicted] of Object.entries(data.predictions)) {
        if (otherActuals[targetUid] !== undefined) {
          if (!perTargetErrors[uid][targetUid]) perTargetErrors[uid][targetUid] = [];
          perTargetErrors[uid][targetUid].push(Math.abs(predicted - otherActuals[targetUid]));
        }
      }
    }

    // How accurately others predicted each player
    for (const uid of playerIds) {
      if (actuals[uid] === undefined) continue;
      let total = 0, count = 0;
      for (const [ouid, odata] of Object.entries(scores)) {
        if (ouid !== uid && odata.predictions?.[uid] !== undefined) {
          total += Math.abs(odata.predictions[uid] - actuals[uid]);
          count++;
        }
      }
      if (count > 0) beingPredictedErrors[uid].push(total / count);
    }

    // Average song rating this round
    const ratings = Object.values(actuals);
    if (ratings.length > 0) {
      songRatings[round.id] = ratings.reduce((a, b) => a + b, 0) / ratings.length;
    }
  }

  // Best friend: for each player, the other player whose scores they predicted most accurately
  const bestFriend = {};
  for (const uid of playerIds) {
    const avgs = {};
    for (const [targetUid, errs] of Object.entries(perTargetErrors[uid])) {
      if (errs.length > 0) avgs[targetUid] = errs.reduce((a, b) => a + b, 0) / errs.length;
    }
    const best = minKeys(avgs);
    bestFriend[uid] = best.length > 0 ? best[0] : null;
  }

  // Best song chosen per player (highest avg-rated song they picked)
  const chooserBestSong = {};
  for (const uid of playerIds) {
    const theirRounds = rounds.filter(
      (r) => r.chooserId === uid && songRatings[r.id] !== undefined
    );
    if (theirRounds.length === 0) { chooserBestSong[uid] = null; continue; }
    const best = theirRounds.reduce((b, r) => (songRatings[r.id] > songRatings[b.id] ? r : b));
    chooserBestSong[uid] = best.song || null;
  }

  const avgPredError = Object.fromEntries(playerIds.map((uid) => [uid, avg(predErrors[uid])]));
  const avgBeingPredError = Object.fromEntries(
    playerIds.map((uid) => [uid, avg(beingPredictedErrors[uid])])
  );

  const maxRoundWins = Math.max(...Object.values(roundWins));
  const mostRoundWins = Object.entries(roundWins)
    .filter(([, v]) => v === maxRoundWins)
    .map(([uid]) => uid);

  // Best taste: chooser whose songs had the highest avg rating
  const chooserAvgRatings = {};
  for (const round of rounds) {
    if (!round.chooserId || songRatings[round.id] === undefined) continue;
    if (!chooserAvgRatings[round.chooserId]) chooserAvgRatings[round.chooserId] = [];
    chooserAvgRatings[round.chooserId].push(songRatings[round.id]);
  }
  const chooserAvg = Object.fromEntries(
    Object.entries(chooserAvgRatings).map(([uid, ratings]) => [uid, avg(ratings)])
  );

  const songEntries = rounds
    .map((r) => ({ round: r, avg: songRatings[r.id] }))
    .filter((x) => x.avg !== undefined);
  const bestSongAvg = songEntries.length ? Math.max(...songEntries.map((x) => x.avg)) : null;
  const worstSongAvg = songEntries.length ? Math.min(...songEntries.map((x) => x.avg)) : null;

  return {
    mostRoundWins,
    bestPredictor: minKeys(avgPredError),
    worstPredictor: maxKeys(avgPredError),
    mostMysterious: maxKeys(avgBeingPredError),
    mostPredictable: minKeys(avgBeingPredError),
    bestTaste: maxKeys(chooserAvg),
    worstTaste: minKeys(chooserAvg),
    bestSongs: songEntries.filter((x) => x.avg === bestSongAvg).map((x) => x.round),
    worstSongs: songEntries.filter((x) => x.avg === worstSongAvg).map((x) => x.round),
    roundWins,
    avgPredError,
    avgBeingPredError,
    chooserAvg,
    bestFriend,
    chooserBestSong,
  };
}
